import React, { useCallback } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { compose } from 'utils';

import { OnClick } from '@rbi-ctg/frontend';
import { ISanityBlockContent, ISanityImage } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import LoadingAnimation from 'components/loading-animation';
import OfferRedemptionModal from 'components/offer-redemption-modal';
import Picture from 'components/picture';
import BlockRenderer from 'components/sanity-block-renderer';
import { IImageFragment, ITrendingFragment } from 'generated/sanity-graphql';
import useMediaQuery from 'hooks/use-media-query';
import { useOffersModal } from 'hooks/use-offers-modal';
import { useAuthContext } from 'state/auth';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useOffersContext } from 'state/offers';
import { isButtonLikeKeypress } from 'utils/event';
import { getMaxPromtions } from 'utils/get-promotions-max-size';
import { getUniqIdForOffer } from 'utils/offers';
import { routes } from 'utils/routing';

import { TrendingHeader } from './header';
import theme from './theme';

const TileLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  min-height: 250px; /* Fixes display bug at mobile sizes with no items */
  padding-block-start: 0;
  padding-block-end: 2rem;
  padding-inline: 0.5rem;

  ${Styles.mobileSmall} {
    grid-template-columns: 1fr;
  }

  ${Styles.desktop} {
    grid-template-columns: 1fr 1fr 1fr;
    max-width: 1270px;
    margin: 0 auto;
    padding-block-start: 0;
    padding-block-end: 1.5rem;
    padding-inline: 0.5rem;
  }
`;

const Wrapper = styled.div`
  cursor: pointer;

  max-width: 400px;
  width: 100%;
  margin: 0.5rem auto;

  background-color: ${theme.wrapperBackgroundColor};
  border-radius: ${Styles.borderRadius};
  box-shadow: ${Styles.boxShadow};
`;

const StyledPicture = styled(Picture)`
  border-radius: ${Styles.borderRadius};
  border-end-start-radius: 0;
  border-end-end-radius: 0;
`;

const Info = styled.div`
  margin: 1.125rem;
  max-height: 100px;
`;

const nameStyles = css`
  font-size: 1.5rem;
  font-family: ${Styles.fontFamily.base};
  color: ${Styles.color.black};
  text-align: start;
  line-height: 1.2;
  text-transform: ${theme.nameTextCase};

  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  -webkit-box-orient: vertical;
`;

const Name = styled.div`
  ${nameStyles}
  margin-block-start: 8px;
  margin-block-end: 4px;
`;

const NameBlockRenderer = styled(BlockRenderer)`
  ${nameStyles};
`;

const descriptionStyles = css`
  font-family: ${Styles.fontFamily.body};
  font-size: 0.9375rem;
  color: ${Styles.color.black};
  text-align: start;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  overflow: hidden;
  -webkit-box-orient: vertical;
`;

const Description = styled.div`
  ${descriptionStyles}
`;

const DescriptionBlockRenderer = styled(BlockRenderer)`
  ${descriptionStyles}
`;

interface ITrendingProps {
  item: ITrendingFragment;
  loading?: boolean;
  header?: boolean;
}

interface IPromotion {
  name?: string | ISanityBlockContent[] | null;
  description?: string | ISanityBlockContent[] | null;
  image?: ISanityImage | IImageFragment | null;
  imageDescription?: string | null;
  onClick: OnClick;
}

const Promotion: React.FC<IPromotion> = ({
  name,
  description,
  image,
  imageDescription,
  onClick,
}) => {
  let nameEl = null;
  if (name) {
    nameEl = typeof name === 'string' ? <Name>{name}</Name> : <NameBlockRenderer content={name} />;
  }

  let descriptionEl = null;
  if (description) {
    descriptionEl =
      typeof description === 'string' ? (
        <Description>{description}</Description>
      ) : (
        <DescriptionBlockRenderer content={description} />
      );
  }

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent) => {
      if (isButtonLikeKeypress(e)) {
        e.preventDefault();
        onClick(e);
      }
    },
    [onClick]
  );

  return (
    <Wrapper role="button" tabIndex={0} aria-pressed="false" onKeyUp={onKeyUp} onClick={onClick}>
      <div>
        <StyledPicture
          image={image}
          placeholderAspectRatio={1.5}
          alt={imageDescription ?? ''}
          objectFitContain={false}
        />
      </div>
      <Info>
        {nameEl}
        {descriptionEl}
      </Info>
    </Wrapper>
  );
};

const Trending: React.FC<ITrendingProps> = ({ item: promotionsList, loading, header = true }) => {
  const isDesktop = useMediaQuery('desktop');
  const { formatMessage } = useIntl();
  const offers = useOffersContext();
  const { Confirm, modalIsOpen, closeModal, makeOfferClickHandler } = useOffersModal(offers);
  const mParticle = useMParticleContext();
  const auth = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();

  const { desktop: maxPromotionsDesktop, mobile: maxPromotionsMobile } = getMaxPromtions();

  if (loading) {
    return <LoadingAnimation fillColor={Styles.color.primary} />;
  }

  const promotions = promotionsList.promotions;
  if (!promotions || promotions.length === 0) {
    return null;
  }

  const displayedPromotions = promotions.slice(
    0,
    isDesktop ? maxPromotionsDesktop : maxPromotionsMobile
  );

  // Don't show the "All trending topics" link if it's already on the /trending page
  const needRedirectLink = location?.pathname !== routes.trending;

  return (
    <>
      {/* Should move header text into sanity and have it controlled by sanity*/}
      {header && <TrendingHeader>{formatMessage({ id: 'trending' })}</TrendingHeader>}
      <TileLayout data-testid="trending-container">
        {displayedPromotions.map(item => {
          if (!item) {
            return null;
          }
          switch (item.__typename) {
            case 'Offer': {
              // only display an offer link if the user is eligible to redeem
              const offer = offers.offers.find(({ _id }) => _id === item._id);
              // offer not found, user cannot redeem
              if (!offer) {
                return null;
              }
              const uniqOfferId = getUniqIdForOffer({ id: offer._id, token: offer.tokenId });
              const onOfferClick = compose(makeOfferClickHandler(uniqOfferId), () => {
                mParticle.trackEvent({
                  name: CustomEventNames.BUTTON_CLICK_PROMOTION_TILE,
                  type: EventTypes.Other,
                  attributes: {
                    'Is Signed In': auth.isAuthenticated(),
                    'Promotion Name': offer?.name?.localeRaw?.[0]?.children?.[0]?.text,
                    'Promotion Description':
                      offer?.description?.localeRaw?.[0]?.children?.[0]?.text,
                  },
                });
              });
              if (!offer.localizedImage?.locale?.app) {
                return null;
              }
              return (
                <Promotion
                  key={offer._id}
                  name={offer.name?.localeRaw}
                  description={offer.description?.localeRaw}
                  image={offer.localizedImage?.locale?.app}
                  imageDescription={offer.localizedImage?.locale?.imageDescription}
                  onClick={onOfferClick}
                />
              );
            }
            case 'ExternalLink': {
              const externalLink = item;
              const isExternalLink = externalLink.link?.locale?.includes('https://');
              return (
                <Promotion
                  key={externalLink._key}
                  name={externalLink.name?.locale}
                  description={externalLink.description?.locale}
                  image={externalLink.image?.locale}
                  imageDescription={externalLink.imageDescription?.locale}
                  onClick={() => {
                    mParticle.trackEvent({
                      name: CustomEventNames.BUTTON_CLICK_PROMOTION_TILE,
                      type: EventTypes.Other,
                      attributes: {
                        'Is Signed In': auth.isAuthenticated(),
                        'Promotion Name': externalLink.name?.locale,
                        'Promotion Description': externalLink.description?.locale,
                        'Promotion Link': externalLink.link?.locale,
                      },
                    });
                    if (!externalLink.link?.locale) {
                      return;
                    }
                    // If the link is an external link, need this logic to navigate the user
                    if (isExternalLink) {
                      window.location.href = externalLink.link?.locale;
                    } else {
                      navigate(externalLink.link?.locale);
                    }
                  }}
                />
              );
            }
            case 'Combo':
            case 'Item':
            case 'Picker':
            case 'Section':
              return (
                <Promotion
                  key={item._id}
                  name={item.name?.locale}
                  description={item.description?.localeRaw}
                  image={item.image}
                  imageDescription={item.imageDescription?.locale}
                  onClick={() => {
                    mParticle.trackEvent({
                      name: CustomEventNames.BUTTON_CLICK_PROMOTION_TILE,
                      type: EventTypes.Other,
                      attributes: {
                        'Is Signed In': auth.isAuthenticated(),
                        'Promotion Name': item.name?.locale,
                        'Promotion Description': item.description?.localeRaw,
                        'Item ID': item._id,
                      },
                    });
                    navigate(`${routes.menu}/${item._type}-${item._id}`);
                  }}
                />
              );
            default:
              return null;
          }
        })}
      </TileLayout>
      {needRedirectLink && displayedPromotions.length !== promotions.length && (
        <Box width="12.75rem" margin="40px 0 60px">
          <ActionButton fullWidth to={routes.trending}>
            {formatMessage({ id: 'allTrendingTopics' })}
          </ActionButton>
        </Box>
      )}
      <OfferRedemptionModal modalIsOpen={modalIsOpen} closeModal={closeModal} />
      <Confirm
        heading={formatMessage({ id: 'oneOfferPerOrder' })}
        body={formatMessage({ id: 'replaceOffer' })}
        confirmLabel={formatMessage({ id: 'yes' })}
        cancelLabel={formatMessage({ id: 'no' })}
      />
    </>
  );
};

export default Trending;
